// The rail's "is my work saved?" line: a colored dot, a short label (Live / Up to date / Syncing… / Saving N /
// Offline / Sync problem / Not saved N) and a Refresh button. Refresh pulls through the running sync engine and
// broadcasts REFRESH_EVENT so screens refetch their own server queries (refresh.ts). "Updated 2 minutes ago" shows
// under "Up to date" only: while live, changes arrive as they sync and the time of the last check says nothing.

import { formatDistanceToNowStrict } from 'date-fns';
import { RefreshCw } from 'lucide-react';
import { useOptionalSyncEngine } from '@/sync/hooks';
import { useSyncStatus } from '@/sync/status';
import { IconButton, Tooltip, cn, useTicker } from '@/ui';
import { refreshNow } from './refresh';
import { describeSync, type SyncTone } from './syncLabel';

const DOT: Record<SyncTone, string> = {
  ok: 'bg-success',
  busy: 'bg-info animate-shimmer',
  offline: 'bg-grey',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

const TEXT: Record<SyncTone, string> = {
  ok: 'text-fg-muted',
  busy: 'text-fg-muted',
  offline: 'text-fg-muted',
  warning: 'text-warning-text',
  danger: 'text-danger-text',
};

export interface SyncIndicatorProps {
  /** Icon-only (collapsed rail, phone top bar): the dot carries the state; the label moves into the tooltip. */
  compact?: boolean;
  /** Compact layout direction: stacked in the rail, side by side in the top bar. */
  direction?: 'column' | 'row';
  /** Override for the Refresh action (defaults to an engine pull plus the gt:refresh broadcast). */
  onRefresh?: () => void;
  className?: string;
}

export function SyncIndicator({ compact = false, direction = 'column', onRefresh, className }: SyncIndicatorProps) {
  const status = useSyncStatus();
  const engine = useOptionalSyncEngine();
  // Re-render every 30 s so "Updated 2 minutes ago" stays true.
  useTicker(30_000);
  const info = describeSync(status);
  const updated =
    info.tone === 'ok' && !status.live && status.lastSyncedAt
      ? `Updated ${formatDistanceToNowStrict(status.lastSyncedAt, { addSuffix: true })}`
      : null;
  const detail = info.detail ?? updated;
  const refresh = onRefresh ?? (() => refreshNow(engine));

  const button = (
    <IconButton
      label="Refresh"
      title={updated ? `Refresh (${updated.toLowerCase()})` : 'Refresh'}
      icon={<RefreshCw className={cn(info.busy && 'animate-spin [animation-duration:1.4s]')} />}
      size="sm"
      onClick={refresh}
    />
  );

  if (compact) {
    return (
      <div className={cn('flex items-center gap-1', direction === 'column' ? 'flex-col' : 'flex-row', className)}>
        <Tooltip
          content={detail ? `${info.label}. ${detail}` : info.label}
          placement={direction === 'column' ? 'right' : 'bottom-end'}
          describe={false}
        >
          <span
            role="img"
            aria-label={detail ? `${info.label}. ${detail}` : info.label}
            className="flex size-8 items-center justify-center"
          >
            <span aria-hidden className={cn('size-2.5 rounded-full', DOT[info.tone])} />
          </span>
        </Tooltip>
        {button}
      </div>
    );
  }

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      {/* Not a live region: the background poll flips this every 30 s and would chatter in a screen reader. */}
      <div className="flex min-w-0 flex-1 items-center gap-2" title={detail ?? undefined}>
        <span aria-hidden className={cn('size-2 shrink-0 rounded-full', DOT[info.tone])} />
        <span className="min-w-0">
          <span className={cn('block truncate text-xs font-semibold', TEXT[info.tone])}>{info.label}</span>
          {detail && <span className="sr-only">{detail}</span>}
          {updated && (
            <span aria-hidden className="block truncate text-[11px] text-fg-subtle">
              {updated}
            </span>
          )}
        </span>
      </div>
      {button}
    </div>
  );
}
